import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import "../css/footer.scss";
import { Icon } from "./Icons";
import siteConfig from "../config/siteConfig";
import productData from "../Datas/productData";

const encodeCategory = (category) =>
  `/shop?category=${encodeURIComponent(category)}`;

function Footer() {
  const brandName = siteConfig.wordmark || siteConfig.brandName;
  const tagline = siteConfig.tagline || "Move lighter. Create anywhere.";
  const description =
    siteConfig.description ||
    "Thoughtful tools for calmer desks, lighter bags and more flexible ways to work.";
  const currencyCode =
    typeof siteConfig.currency === "string"
      ? siteConfig.currency
      : siteConfig.currency?.code || "USD";
  const localeLabel = String(siteConfig.locale || "en-US")
    .split("-")[0]
    .toUpperCase();

  const shopLinks = useMemo(() => {
    const categories = Array.from(
      new Set(
        (Array.isArray(productData) ? productData : [])
          .map((product) => product?.category)
          .filter(Boolean)
      )
    );
    const configuredShopLinks = (siteConfig.navigation || [])
      .filter((item) => item?.path?.startsWith("/shop") && item.label)
      .map((item) => ({ label: item.label, path: item.path }));
    const categoryLinks = categories.map((category) => ({
      label: category,
      path: encodeCategory(category),
    }));

    const uniqueLinks = [
      { label: "Shop All", path: "/shop" },
      ...configuredShopLinks,
      ...categoryLinks,
    ].filter(
      (link, index, links) =>
        links.findIndex((candidate) => candidate.label === link.label) === index
    );

    return uniqueLinks.slice(0, 5);
  }, []);

  const footerSections = [
    {
      title: "Shop",
      links: shopLinks,
    },
    {
      title: "Customer Care",
      links: [
        { label: "Contact Us", path: "/contact" },
        { label: "FAQs", path: "/faqs" },
        { label: "Shipping & Delivery", path: "/shipping" },
        { label: "Returns", path: "/returns" },
        { label: "Your Cart", path: "/cartpage" },
      ],
    },
    {
      title: "About",
      links: [
        { label: "Our Story", path: "/about" },
        { label: "Designed to Move", path: "/about#philosophy" },
        { label: "Design Approach", path: "/about#design" },
        { label: "Get in Touch", path: "/contact" },
      ],
    },
    {
      title: "Legal",
      links: [
        { label: "Privacy Policy", path: "/privacy" },
        { label: "Cookie Policy", path: "/cookies" },
        { label: "Terms of Service", path: "/terms" },
        { label: "Shipping Policy", path: "/shipping" },
        { label: "Return Policy", path: "/returns" },
      ],
    },
  ];

  return (
    <footer className="site-footer" role="contentinfo">
      <div className="site-footer__top">
        <div className="site-footer__statement">
          <span className="site-footer__eyebrow">{brandName}</span>
          <h2>{tagline}</h2>
          <p>{description}</p>
          <Link className="site-footer__story-link" to="/about">
            Discover our story
            <Icon name="arrow" size={17} />
          </Link>
        </div>

        <div className="site-footer__support">
          <span className="site-footer__eyebrow">Customer care</span>
          <h2>Useful help, when you need it.</h2>
          <p>
            Product guidance, delivery questions and order support from a real
            person.
          </p>
          <a
            className="site-footer__story-link"
            href={`mailto:${siteConfig.contact.supportEmail}`}
          >
            {siteConfig.contact.supportEmail}
            <Icon name="arrow" size={17} />
          </a>
        </div>
      </div>

      <div className="site-footer__navigation" aria-label="Footer navigation">
        {footerSections.map((section) => (
          <nav aria-label={section.title} key={section.title}>
            <h2>{section.title}</h2>
            <ul>
              {section.links.map((link) => (
                <li key={`${section.title}-${link.label}`}>
                  <Link to={link.path}>{link.label}</Link>
                </li>
              ))}
            </ul>
          </nav>
        ))}
      </div>

      <div
        className="site-footer__payments"
        aria-label="Supported payment methods"
      >
        <span>Secure payments</span>
        <ul>
          {(siteConfig.paymentMethods || []).map((method) => (
            <li key={method}>{method}</li>
          ))}
        </ul>
      </div>

      <div className="site-footer__bottom">
        <Link
          className="site-footer__wordmark"
          to="/"
          aria-label={`${brandName} home`}
        >
          {brandName}
        </Link>
        <p>
          © {new Date().getFullYear()} {brandName}. All rights reserved.
          <br />
          {siteConfig.contact.address} · {siteConfig.contact.email}
        </p>
        <div className="site-footer__meta">
          <span>
            <Icon name="globe" size={16} /> {localeLabel} / {currencyCode}
          </span>
          <span>
            {Array.isArray(productData) ? productData.length : 0} considered
            essentials
          </span>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
